import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  LinearProgress,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useAntiCheat from '../../hooks/useAntiCheat';

// Componentes estilizados
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
  paddingTop: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
}));

const TimerCard = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  top: 20,
  right: 20,
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(1),
  zIndex: 1000,
  // Móvil: en vez de flotar sobre el título, va pegado arriba dentro del flujo
  // (sticky): acompaña el scroll sin tapar el encabezado al inicio. Compacto y
  // semitransparente para no molestar; cuando queda poco tiempo (prop `urgent`)
  // se vuelve sólido y cambia a color de alerta.
  [theme.breakpoints.down('sm')]: {
    position: 'sticky',
    top: theme.spacing(1),
    right: 'auto',
    width: 'fit-content',
    marginLeft: 'auto',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5, 1.25),
    backgroundColor: 'rgba(255, 255, 255, 0.82)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: theme.shadows[2],
    '& .MuiSvgIcon-root': { fontSize: 18 },
    '& .MuiTypography-root': { fontSize: '0.9rem' },
  },
  '&.timer-urgent': {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.error.main,
    '& .MuiSvgIcon-root, & .MuiTypography-root': { color: theme.palette.error.main },
  },
}));

const QuestionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
}));

const CaseStudyCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  border: `2px solid ${theme.palette.info.main}`,
  backgroundColor: theme.palette.info.main + '10',
}));

const CaseStudyContent = styled(Box)(({ theme }) => ({
  maxHeight: '300px',
  overflowY: 'auto',
  // Móvil: más alto para leer el caso sin scroll dentro de scroll
  [theme.breakpoints.down('sm')]: {
    maxHeight: '60vh',
  },
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.primary.main} ${theme.palette.grey[100]}`,
}));

// ---------------------------------------------------------------------------
// Un caso, varias competencias (PLAN_CASOS_ESTUDIO.md §6 / §8)
// El candidato recorre una secuencia de pasos:
//   [reading(caso 1), questions(comp A), questions(comp C), reading(caso 2), questions(comp B), ...]
// El backend entrega `caseStudies[]`: agrupado si la respuesta es nueva, o
// derivado "un caso por competencia" (legacy: true) si es antigua. Para una
// respuesta antigua la secuencia es exactamente la de antes:
// reading(comp 1) → questions(comp 1) → reading(comp 2) → ...
// ---------------------------------------------------------------------------
const tsToMs = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp._seconds) return timestamp._seconds * 1000;
  const ms = new Date(timestamp).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const legacyCaseStudyFor = (cap, index) => ({
  caseStudyId: `legacy:${cap.capabilityId || 'cap'}:${index}`,
  title: cap.caseStudy?.title || '',
  content: cap.caseStudy?.content || '',
  readingTime: cap.readingTime || 5,
  capabilityIds: [cap.capabilityId],
  readingStartedAt: cap.readingStartedAt || null,
  readingCompletedAt: cap.readingCompletedAt || null,
  legacy: true,
});

// Casos de estudio de la respuesta. Si el backend no los entrega (o alguna
// competencia quedó fuera de todo caso), se completa con un caso legacy por
// competencia — mismo comportamiento que antes de la agrupación.
const deriveCaseStudies = (evaluationData) => {
  const caps = evaluationData?.capabilities || [];
  const provided = Array.isArray(evaluationData?.caseStudies) ? evaluationData.caseStudies : [];
  const result = provided
    .map((cs) => ({ ...cs, capabilityIds: (cs.capabilityIds || []).filter((id) => caps.some((c) => c.capabilityId === id)) }))
    .filter((cs) => cs.capabilityIds.length > 0);
  // Sin Array.prototype.flatMap: CRA no lo polyfilla y en navegadores antiguos
  // (Chrome < 69, Safari < 12, WebViews viejos) lanzaba TypeError → página en blanco
  const covered = new Set();
  result.forEach((cs) => cs.capabilityIds.forEach((id) => covered.add(id)));
  caps.forEach((cap, index) => {
    if (!covered.has(cap.capabilityId)) result.push(legacyCaseStudyFor(cap, index));
  });
  return result;
};

const buildSteps = (caseStudies, capabilities) => {
  const steps = [];
  caseStudies.forEach((cs, caseIndex) => {
    const capIndices = (cs.capabilityIds || [])
      .map((id) => capabilities.findIndex((c) => c.capabilityId === id))
      .filter((i) => i >= 0);
    if (capIndices.length === 0) return;
    steps.push({ type: 'reading', caseIndex, competencyIndex: capIndices[0] });
    capIndices.forEach((competencyIndex) => steps.push({ type: 'questions', caseIndex, competencyIndex }));
  });
  return steps;
};

const PublicEvaluationQuestions = ({ responseId, applicantEmail, evaluationData, onComplete, language }) => {
  // Estados simples
  const [currentCompetencyIndex, setCurrentCompetencyIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('reading');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(0);
  const [answers, setAnswers] = useState({});
  const [completing, setCompleting] = useState(false);
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [error, setError] = useState(null);
  const [isAutoCompleting, setIsAutoCompleting] = useState(false);
  const [showCompletingModal, setShowCompletingModal] = useState(false);

  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const phaseStartTime = useRef(null);
  const autoSaveTimeouts = useRef({});

  // Tiempo acumulado POR PREGUNTA, en segundos, con clave `${capIndex}-${qIndex}`.
  // Antes se enviaba el tiempo transcurrido de la FASE completa, con lo cual todas
  // las preguntas de una competencia terminaban con el mismo valor (y con el límite
  // de la fase cuando el tiempo expiraba).
  // Se mide con reloj real (Date.now) y no contando ticks, porque el navegador
  // ralentiza los setInterval en pestañas en segundo plano.
  const questionTimeSpent = useRef({});
  const activeQuestionKey = useRef(null);
  const activeQuestionSince = useRef(null);

  // Ref que siempre apunta a la versión más reciente de handlePhaseComplete.
  // El setInterval de startPhase captura el closure del render en que se creó:
  // si al expirar el tiempo llamara handlePhaseComplete directo, usaría un
  // `answers` congelado al inicio de la fase y el auto-complete pisaría en el
  // backend (con placeholder) respuestas reales ya auto-guardadas.
  // Se reasigna en cada render (ver línea tras la definición de handlePhaseComplete).
  const handlePhaseCompleteRef = useRef(null);

  const { t } = useTranslation();
  const capabilities = useMemo(() => evaluationData?.capabilities || [], [evaluationData]);
  const currentCapability = capabilities[currentCompetencyIndex] || {};

  // Casos de estudio y secuencia de pasos (ver comentario al inicio del archivo)
  const caseStudies = useMemo(() => deriveCaseStudies(evaluationData), [evaluationData]);
  const steps = useMemo(() => buildSteps(caseStudies, capabilities), [caseStudies, capabilities]);
  const isGrouped = caseStudies.some((cs) => !cs.legacy);
  const currentStep = steps[currentStepIndex] || null;
  const currentCaseStudy = (currentStep && caseStudies[currentStep.caseIndex]) || caseStudies[0] || {};
  // Índice de la competencia dentro de su caso (para el encabezado en modo agrupado)
  const competencyIndexInCase = currentCaseStudy.capabilityIds
    ? currentCaseStudy.capabilityIds.indexOf(currentCapability.capabilityId)
    : -1;

  const { getEventsSummary, resetEvents } = useAntiCheat({
    enabled: true,
    userId: applicantEmail,
    testId: responseId
  });

  const savedPhasesRef = useRef(new Set());

  // Consolidar en el acumulador el tiempo transcurrido en la pregunta activa.
  // Se llama desde el timer, al perder el foco, al cambiar de fase y antes de
  // cada guardado, de modo que el valor esté siempre al día.
  const flushActiveQuestionTime = () => {
    if (!activeQuestionKey.current || !activeQuestionSince.current) return;

    const key = activeQuestionKey.current;
    const elapsed = Math.round((Date.now() - activeQuestionSince.current) / 1000);

    if (elapsed > 0) {
      questionTimeSpent.current[key] = (questionTimeSpent.current[key] || 0) + elapsed;
      activeQuestionSince.current = Date.now();
    }
  };

  const setActiveQuestion = (key) => {
    flushActiveQuestionTime();
    activeQuestionKey.current = key;
    activeQuestionSince.current = key ? Date.now() : null;
  };

  // Tiempo acumulado en una pregunta puntual
  const getQuestionTimeSpent = (key) => {
    flushActiveQuestionTime();
    return questionTimeSpent.current[key] || 0;
  };

  // Endpoint + payload de fase para un paso:
  // - questions → capabilities/:capabilityId/phase (phase 'questions'), como siempre
  // - reading legacy → capabilities/:capabilityId/phase (phase 'reading'), como siempre
  // - reading agrupada → case-studies/:caseStudyId/phase (lectura una vez por caso)
  const phaseRequestFor = (step) => {
    const capability = capabilities[step.competencyIndex] || {};
    const caseStudy = caseStudies[step.caseIndex] || {};
    if (step.type === 'reading' && !caseStudy.legacy) {
      return {
        url: `/public/assessment2/evaluation-response/${responseId}/case-studies/${caseStudy.caseStudyId}/phase`,
        body: {},
        key: `case-${caseStudy.caseStudyId}-reading`,
      };
    }
    return {
      url: `/public/assessment2/evaluation-response/${responseId}/capabilities/${capability.capabilityId}/phase`,
      body: { phase: step.type },
      key: `${capability.capabilityId}-${step.type}`,
    };
  };

  // Guardar timestamp de inicio de fase en backend
  const savePhaseStart = async (step) => {
    try {
      const { url, body } = phaseRequestFor(step);
      await api.put(url, { applicantEmail, ...body, action: 'start' });
    } catch (err) {
      console.error('Error saving phase start:', err);
    }
  };

  // Guardar completado de fase en backend
  const savePhaseComplete = async (step) => {
    const { url, body, key } = phaseRequestFor(step);

    if (savedPhasesRef.current.has(key)) {
      return;
    }

    try {
      const antiCheatEvents = getEventsSummary();
      await api.put(url, { applicantEmail, ...body, action: 'complete', antiCheatEvents });
      savedPhasesRef.current.add(key);
      resetEvents();
    } catch (err) {
      console.error('Error saving phase complete:', err);
    }
  };

  // Inicializar un paso (lectura de un caso o preguntas de una competencia)
  const startStep = async (stepIndex) => {
    const step = steps[stepIndex];
    if (!step) {
      console.error(`No step found at index ${stepIndex}`);
      return;
    }
    const { type: phase, competencyIndex } = step;
    const capability = capabilities[competencyIndex];
    const caseStudy = caseStudies[step.caseIndex] || {};
    if (!capability) {
      console.error(`No capability found at index ${competencyIndex}`);
      return;
    }

    // Limpiar cualquier timer anterior
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setCurrentStepIndex(stepIndex);
    setCurrentPhase(phase);
    setCurrentCompetencyIndex(competencyIndex);

    // Determinar timestamp de inicio: la lectura es del caso (en legacy el caso
    // derivado trae el readingStartedAt de la propia competencia)
    const existingTimestamp = phase === 'reading' ? caseStudy.readingStartedAt : capability.answeringStartedAt;
    let startTime = tsToMs(existingTimestamp);
    if (!startTime) {
      startTime = Date.now();
      await savePhaseStart(step);
    }
    phaseStartTime.current = startTime;

    // Al cambiar de fase/competencia se consolida lo acumulado y ninguna
    // pregunta queda activa hasta que el candidato ponga el foco en una.
    setActiveQuestion(null);

    // Calcular tiempo restante en segundos
    const timeLimit = phase === 'reading' ? caseStudy.readingTime : capability.questionTime;
    const timeLimitMs = (timeLimit || 5) * 60 * 1000;
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, Math.floor((timeLimitMs - elapsed) / 1000));
    setPhaseTimeRemaining(remaining);

    // Si ya expiró, completar de inmediato
    if (remaining === 0) {
      handlePhaseCompleteRef.current(stepIndex);
      return;
    }

    // Crear timer
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const elapsedMs = now - phaseStartTime.current;
      const remainingSecs = Math.max(0, Math.floor((timeLimitMs - elapsedMs) / 1000));
      setPhaseTimeRemaining(remainingSecs);

      // Consolidar el tiempo transcurrido en la pregunta que se está respondiendo
      if (phase === 'questions') {
        flushActiveQuestionTime();
      }

      if (remainingSecs <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        // Usar el ref para invocar la versión vigente (no la del render en que
        // se creó el interval), con answers/estados actualizados
        handlePhaseCompleteRef.current(stepIndex);
      }
    }, 1000);
  };


  // Determinar paso inicial basado en tiempo global transcurrido (retomar
  // tras refresh): se recorre la secuencia acumulando la duración de cada paso.
  const determineInitialStepIndex = () => {
    // Buscar el primer timestamp de inicio (lectura del primer caso)
    let evaluationStartTime = null;

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (step.type !== 'reading') continue;
      const startedAt = tsToMs(caseStudies[step.caseIndex]?.readingStartedAt);
      if (startedAt) {
        evaluationStartTime = startedAt;
        break;
      }
    }

    if (!evaluationStartTime) {
      return 0;
    }

    const now = Date.now();
    const totalElapsedSeconds = Math.floor((now - evaluationStartTime) / 1000);
    let timeAccumulator = 0;

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const stepSeconds = step.type === 'reading'
        ? (caseStudies[step.caseIndex]?.readingTime || 5) * 60
        : (capabilities[step.competencyIndex]?.questionTime || 5) * 60;

      if (totalElapsedSeconds < timeAccumulator + stepSeconds) {
        return i;
      }

      timeAccumulator += stepSeconds;
    }

    return steps.length - 1;
  };

  // Inicialización
  useEffect(() => {
    if (!capabilities.length) return;

    // Verificar expiración usando expiresAt
    if (evaluationData.expiresAt) {
      const expirationTime = evaluationData.expiresAt._seconds * 1000;
      const now = Date.now();

      if (now >= expirationTime) {
        if (evaluationData.overallStatus !== 'completed') {
          // Mostrar modal INMEDIATAMENTE
          setShowCompletingModal(true);

          // Auto-completar evaluación expirada.
          // No se reenvían respuestas: el backend ya tiene las guardadas
          // (auto-guardado por pregunta) y las preguntas sin responder quedan
          // en `pending`, que el backend puntúa como 0. Antes se reenviaba todo
          // con placeholder "Tiempo agotado" para las vacías, lo que
          // contaminaba el análisis.
          (async () => {
            try {
              await api.put(`/public/assessment2/evaluation-response/${responseId}/complete`, {
                applicantEmail,
                language
              });

              setShowCompletingModal(false);
              onComplete(true);
            } catch (err) {
              console.error('Error completing expired evaluation:', err);
              setShowCompletingModal(false);
              onComplete(true);
            }
          })();
        } else {
          onComplete(true);
        }
        return;
      }
    }

    // Resto del código normal...
    const initialAnswers = {};
    capabilities.forEach((cap, capIndex) => {
      cap.questions.forEach((q, qIndex) => {
        const key = `${capIndex}-${qIndex}`;
        initialAnswers[key] = q.answerText || '';
        // Al retomar el assessment se conserva el tiempo ya acumulado en el backend
        questionTimeSpent.current[key] = q.timeSpent || 0;
      });
    });
    setAnswers(initialAnswers);

    startStep(determineInitialStepIndex());

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      Object.values(autoSaveTimeouts.current).forEach(entry => clearTimeout(entry.timeout));
      autoSaveTimeouts.current = {};
    };
  }, [capabilities.length]);

  // Manejar fin de paso (tiempo agotado)
  const handlePhaseComplete = async (stepIndex = currentStepIndex) => {
    if (isAutoCompleting || completing) return;

    const step = steps[stepIndex];
    if (!step) return;

    // Persistir lo que quedó en debounce antes de cerrar la fase
    if (step.type === 'questions') {
      await flushPendingAnswerSaves();
    }

    await savePhaseComplete(step);

    if (stepIndex < steps.length - 1) {
      // Siguiente paso: preguntas de la competencia (tras la lectura), la
      // siguiente competencia del mismo caso, o la lectura del siguiente caso
      startStep(stepIndex + 1);
    } else {
      // Último paso: finalizar evaluación automáticamente
      await handleAutoComplete();
    }
  };

  // Reasignar en cada render para que el interval de startStep (y la ruta de
  // expiración inmediata) siempre ejecuten la versión vigente del handler
  handlePhaseCompleteRef.current = handlePhaseComplete;


  // Completar automáticamente cuando se acaba el tiempo
  const handleAutoComplete = async () => {
    // PROTECCIÓN: Evitar múltiples ejecuciones
    if (isAutoCompleting || completing) {
      return;
    }

    setIsAutoCompleting(true);
    setShowCompletingModal(true);
    console.log('Auto-completing evaluation due to time expiration...');

    // DETENER TODOS LOS TIMERS INMEDIATAMENTE
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoSaveRef.current) {
      clearTimeout(autoSaveRef.current);
      autoSaveRef.current = null;
    }

    // NUEVO: Limpiar todos los timeouts de auto-guardado pendientes
    Object.values(autoSaveTimeouts.current).forEach(entry => clearTimeout(entry.timeout));
    autoSaveTimeouts.current = {};

    try {
      // Guardar solo respuestas con contenido real. NO enviar placeholders
      // ("Tiempo agotado"): sobrescribían respuestas ya auto-guardadas en el
      // backend y además pasaban el filtro del /complete, contaminando el
      // análisis GPT como si fueran respuestas del candidato. Las preguntas
      // vacías quedan en `pending` y el backend las puntúa como 0.
      //
      // El guardado es SECUENCIAL a propósito: cada PUT reescribe el array
      // completo de capabilities en Firestore, así que enviarlos en paralelo
      // hacía que las escrituras se pisaran entre sí y se perdieran respuestas.
      const pendingSaves = [];
      capabilities.forEach((cap, capIndex) => {
        cap.questions.forEach((q, qIndex) => {
          const key = `${capIndex}-${qIndex}`;
          const answer = answers[key] || '';

          if (!answer.trim()) return;

          pendingSaves.push({
            capabilityId: cap.capabilityId,
            order: q.order,
            answerText: answer,
            timeSpent: getQuestionTimeSpent(key)
          });
        });
      });

      for (const save of pendingSaves) {
        try {
          await api.put(`/public/assessment2/evaluation-response/${responseId}/competencies/${save.capabilityId}/questions/${save.order}`, {
            applicantEmail,
            answerText: save.answerText,
            timeSpent: save.timeSpent
          });
        } catch (saveErr) {
          // Una respuesta que falla no debe impedir guardar las demás
          console.error(`Error guardando respuesta ${save.capabilityId}/${save.order}:`, saveErr);
        }
      }

      const antiCheatEvents = getEventsSummary();

      await api.put(`/public/assessment2/evaluation-response/${responseId}/complete`, {
        applicantEmail,
        language,
        antiCheatEvents
      });

      // Mostrar mensaje y completar
      setError(language === 'en' ? 'Time expired - Evaluation completed automatically' : 'Tiempo agotado - Evaluación completada automáticamente');

      setTimeout(() => {
        onComplete(true); // true indica que expiró por tiempo
      }, 2000);

      setShowCompletingModal(false);

    } catch (err) {
      console.error('Error auto-completing evaluation:', err);
      setError(language === 'en' ? 'Error completing evaluation automatically' : 'Error completando evaluación automáticamente');
      setShowCompletingModal(false);
    } finally {
      setIsAutoCompleting(false);
    }
  };

  // Avanzar manualmente
  const handleAdvanceToQuestions = async () => {
    if (currentPhase === 'reading' && currentStep) {
      // Limpiar timer actual antes de avanzar
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      await savePhaseComplete(currentStep);
      startStep(currentStepIndex + 1);
    }
  };

  const handleAdvanceToNext = async () => {
    if (!currentStep) return;

    // Limpiar timer actual antes de avanzar
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Persistir lo que quedó en debounce antes de cambiar de competencia
    await flushPendingAnswerSaves();

    await savePhaseComplete(currentStep);

    if (currentStepIndex < steps.length - 1) {
      startStep(currentStepIndex + 1);
    } else {
      setShowFinishDialog(true);
    }
  };

  // Manejar cambio de respuesta
  const handleAnswerChange = (questionIndex, value) => {
    const key = `${currentCompetencyIndex}-${questionIndex}`;
    setAnswers(prev => ({ ...prev, [key]: value }));

    const timeoutKey = key;
    const question = currentCapability.questions[questionIndex];
    const capabilityId = currentCapability.capabilityId;

    if (autoSaveTimeouts.current[timeoutKey]) {
      clearTimeout(autoSaveTimeouts.current[timeoutKey].timeout);
    }

    const timeout = setTimeout(async () => {
      const pending = autoSaveTimeouts.current[timeoutKey];
      delete autoSaveTimeouts.current[timeoutKey];
      if (!pending) return;

      try {
        const timeSpent = getQuestionTimeSpent(key); // tiempo real de ESTA pregunta

        await api.put(`/public/assessment2/evaluation-response/${responseId}/competencies/${capabilityId}/questions/${question.order}`, {
          applicantEmail,
          answerText: value,
          timeSpent: timeSpent
        });
        console.log(`Auto-saved answer for question ${question.order} with ${timeSpent}s`);
      } catch (err) {
        console.error('Auto-save error:', err);
      }
    }, 2000);

    // Se guarda el payload junto al timeout para poder forzar el guardado
    // antes de avanzar de fase (ver flushPendingAnswerSaves).
    autoSaveTimeouts.current[timeoutKey] = {
      timeout,
      capabilityId,
      order: question.order,
      value,
      key
    };
  };

  // Ejecutar de inmediato los auto-guardados con debounce pendientes.
  // Sin esto, avanzar de competencia dentro de los 2 segundos posteriores a
  // escribir dejaba la respuesta sin guardar o la hacía competir con el PUT de
  // fase sobre el mismo documento.
  const flushPendingAnswerSaves = async () => {
    const pending = Object.values(autoSaveTimeouts.current);
    if (pending.length === 0) return;

    pending.forEach(entry => clearTimeout(entry.timeout));
    autoSaveTimeouts.current = {};

    for (const entry of pending) {
      try {
        await api.put(`/public/assessment2/evaluation-response/${responseId}/competencies/${entry.capabilityId}/questions/${entry.order}`, {
          applicantEmail,
          answerText: entry.value,
          timeSpent: getQuestionTimeSpent(entry.key)
        });
      } catch (err) {
        console.error('Error guardando respuesta pendiente:', err);
      }
    }
  };

  // Completar evaluación
  const handleComplete = async () => {
    setCompleting(true);
    setShowCompletingModal(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoSaveRef.current) {
      clearTimeout(autoSaveRef.current);
      autoSaveRef.current = null;
    }

    Object.values(autoSaveTimeouts.current).forEach(entry => clearTimeout(entry.timeout));
    autoSaveTimeouts.current = {};

    try {
      // Igual que en el auto-complete: sólo se envían respuestas con contenido real.
      // Antes se guardaba el placeholder "Sin respuesta" en la base, lo que dejaba
      // la pregunta marcada como respondida y contaminaba el análisis. Ahora las
      // preguntas vacías quedan en `pending` y el backend las puntúa como 0.
      //
      // El guardado es SECUENCIAL para no pisar escrituras concurrentes sobre el
      // mismo documento (cada PUT reescribe el array completo de capabilities).
      const pendingSaves = [];
      capabilities.forEach((cap, capIndex) => {
        cap.questions.forEach((q, qIndex) => {
          const key = `${capIndex}-${qIndex}`;
          const answer = answers[key] || '';

          if (!answer.trim()) return;

          pendingSaves.push({
            capabilityId: cap.capabilityId,
            order: q.order,
            answerText: answer,
            timeSpent: getQuestionTimeSpent(key)
          });
        });
      });

      for (const save of pendingSaves) {
        try {
          await api.put(`/public/assessment2/evaluation-response/${responseId}/competencies/${save.capabilityId}/questions/${save.order}`, {
            applicantEmail,
            answerText: save.answerText,
            timeSpent: save.timeSpent
          });
        } catch (saveErr) {
          console.error(`Error guardando respuesta ${save.capabilityId}/${save.order}:`, saveErr);
        }
      }

      const antiCheatEvents = getEventsSummary();

      await api.put(`/public/assessment2/evaluation-response/${responseId}/complete`, {
        applicantEmail,
        language,
        antiCheatEvents
      });

      onComplete();
    } catch (err) {
      setError('Error completing evaluation');
    } finally {
      setCompleting(false);
      setShowFinishDialog(false);
    }
  };

  // Formatear tiempo
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calcular progreso
  const answeredCount = currentCapability.questions?.filter((_, i) => {
    const key = `${currentCompetencyIndex}-${i}`;
    return answers[key]?.trim();
  }).length || 0;

  // Renderizar fase de lectura
  if (currentPhase === 'reading') {
    return (
      <StyledContainer>
        <TimerCard className={phaseTimeRemaining <= 60 ? 'timer-urgent' : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBookIcon color="primary" />
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {formatTime(phaseTimeRemaining)}
            </Typography>
          </Box>
        </TimerCard>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            {isGrouped ? (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('publicEvaluations.caseStudyOf', { current: (currentStep?.caseIndex || 0) + 1, total: caseStudies.length })}
                  {currentCaseStudy.title ? `: ${currentCaseStudy.title}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {(currentCaseStudy.capabilityIds || []).map((capId) => {
                    const cap = capabilities.find((c) => c.capabilityId === capId);
                    return cap ? (
                      <Chip key={capId} size="small" variant="outlined" color="primary" label={cap.capabilityName} />
                    ) : null;
                  })}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('publicEvaluations.caseStudyCompetenciesHint', { count: (currentCaseStudy.capabilityIds || []).length })}
                </Typography>
              </>
            ) : (
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('publicEvaluations.competency')} {currentCompetencyIndex + 1} {t('publicEvaluations.of')} {capabilities.length}: {currentCapability.capabilityName}
              </Typography>
            )}
            <LinearProgress
              variant="determinate"
              value={isGrouped
                ? (steps.length ? (currentStepIndex / steps.length) * 100 : 0)
                : (currentCompetencyIndex / capabilities.length) * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <CaseStudyCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <MenuBookIcon color="info" />
              <Typography variant="h6" color="info.main">
                {currentCaseStudy.title || currentCapability.caseStudy?.title || t('publicEvaluations.caseStudy')}
              </Typography>
            </Box>

            <CaseStudyContent>
              <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {currentCaseStudy.content || currentCapability.caseStudy?.content || 'Contenido del caso de estudio...'}
              </Typography>
            </CaseStudyContent>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t('publicEvaluations.readInstructions', { minutes: currentCaseStudy.readingTime || currentCapability.readingTime })}
              </Typography>
            </Alert>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleAdvanceToQuestions}
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 4 }}
              >
                {t('publicEvaluations.continueToQuestions')}
              </Button>
            </Box>
          </CardContent>
        </CaseStudyCard>
        {/* Modal de completando evaluación */}
        <Dialog
          open={showCompletingModal}
          disableEscapeKeyDown
          disableBackdropClick
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(5px)',
            }
          }}
          slotProps={{
            backdrop: {
              style: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              }
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('publicEvaluations.completingEvaluation')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('publicEvaluations.processingResponses')}
              <br />
              {t('publicEvaluations.operationTime')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
              {t('publicEvaluations.doNotClose')}
            </Typography>
          </DialogContent>
        </Dialog>
      </StyledContainer>
    );
  }

  // Renderizar fase de preguntas
  return (
    <StyledContainer>
      <TimerCard className={phaseTimeRemaining <= 60 ? 'timer-urgent' : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimerIcon color="primary" />
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {formatTime(phaseTimeRemaining)}
          </Typography>
        </Box>
      </TimerCard>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6">
                {currentCapability.capabilityName} - {t('publicEvaluations.questions')}
              </Typography>
              {isGrouped && currentCaseStudy.title && (
                <Typography variant="caption" color="text.secondary">
                  {t('publicEvaluations.caseStudy')}: {currentCaseStudy.title}
                  {competencyIndexInCase >= 0 && (currentCaseStudy.capabilityIds || []).length > 1 && (
                    <> · {t('publicEvaluations.competencyOfCase', { current: competencyIndexInCase + 1, total: currentCaseStudy.capabilityIds.length })}</>
                  )}
                </Typography>
              )}
            </Box>
            <Chip
              label={`${answeredCount}/${currentCapability.questions?.length || 0} ${t('publicEvaluations.answered')}`}
              color={answeredCount === currentCapability.questions?.length ? 'success' : 'default'}
              icon={answeredCount === currentCapability.questions?.length ? <CheckCircleIcon /> : undefined}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={currentCapability.questions?.length ? (answeredCount / currentCapability.questions.length) * 100 : 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {currentCapability.questions?.map((question, qIndex) => (
        <QuestionCard key={qIndex}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              {t('publicEvaluations.question')} {qIndex + 1}
            </Typography>

            <Typography variant="h5" sx={{ mb: 4, lineHeight: 1.6 }}>
              {question.text}
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder={t('publicEvaluations.writePlaceholder')}
              value={answers[`${currentCompetencyIndex}-${qIndex}`] || ''}
              onChange={(e) => handleAnswerChange(qIndex, e.target.value)}
              onFocus={() => setActiveQuestion(`${currentCompetencyIndex}-${qIndex}`)}
              onBlur={() => {
                if (activeQuestionKey.current === `${currentCompetencyIndex}-${qIndex}`) {
                  setActiveQuestion(null);
                }
              }}
              sx={{ mb: 2 }}
            />
          </CardContent>
        </QuestionCard>
      ))}

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        {currentStepIndex >= steps.length - 1 ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => setShowFinishDialog(true)}
            sx={{ px: 4 }}
            size="large"
          >
            {t('publicEvaluations.finishEvaluation')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleAdvanceToNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ px: 4 }}
            size="large"
          >
            {t('publicEvaluations.nextCompetency')}
          </Button>
        )}
      </Box>

      <Dialog open={showFinishDialog} onClose={() => setShowFinishDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {t('publicEvaluations.finishEvaluation')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('publicEvaluations.confirmFinish')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('publicEvaluations.noReturn')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFinishDialog(false)} disabled={completing}>
            {t('publicEvaluations.cancel')}
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="success"
            disabled={completing}
            startIcon={completing ? <CircularProgress size={20} /> : undefined}
          >
            {completing ? t('publicEvaluations.finishing') : t('publicEvaluations.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal de completando evaluación */}
      <Dialog
        open={showCompletingModal}
        disableEscapeKeyDown
        disableBackdropClick
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(5px)',
          }
        }}
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('publicEvaluations.completingEvaluation')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('publicEvaluations.processingResponses')}
            <br />
            {t('publicEvaluations.operationTime')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            {t('publicEvaluations.doNotClose')}
          </Typography>
        </DialogContent>
      </Dialog>
    </StyledContainer>
  );
};

export default PublicEvaluationQuestions;